import {
  CalculatedBalanceCollection,
  CalculatedBalancesArray,
  ScenarioBalanceParams,
} from "@bitwarden/web-vault/app/services/store/calculated-balance.collection.abstraction";
import { signal } from "@angular/core";
import { BalancePoint } from "@bitwarden/web-vault/app/models/types/balanceGroupingTypes";

export class ScenarioBalanceByTimeCalculatedCollection extends CalculatedBalanceCollection {
  protected readonly type = "ScenarioBalanceByTime";
  protected calculations: CalculatedBalancesArray = [];
  protected calculationsIns: CalculatedBalancesArray = [];
  protected calculationsOuts: CalculatedBalancesArray = [];
  protected balancePointsArray: Map<string, BalancePoint>[] = [];

  protected _balances = signal<CalculatedBalancesArray>(null);
  protected _balancePoints = signal<Map<string, BalancePoint>[]>(null);
  readonly balances = this._balances.asReadonly();
  readonly balancePoints = this._balancePoints.asReadonly();
  protected _ins = signal<CalculatedBalancesArray>(null);
  readonly ins = this._ins.asReadonly();
  protected _outs = signal<CalculatedBalancesArray>(null);
  readonly outs = this._outs.asReadonly();

  /**
   * Uses the filteredTransactions stored on the calculation store to update
   * the calculations for balance by account
   */
  async updateCalculations(parameters: ScenarioBalanceParams): Promise<void> {
    const isStale = this.staleCheck();
    const promises: Promise<BalancePoint[]>[] = [];
    for (const transactions of parameters.scenarioTransactions) {
      promises.push(
        this.getBalanceByTime(
          parameters.webWorkerQueue,
          transactions,
          parameters.period.startDate,
          parameters.period.endDate,
          parameters.group.granularity,
        ),
      );
    }
    const results: BalancePoint[][] = await Promise.all(promises);
    if (isStale()) {
      return;
    }
    this.setBalances(results);
  }

  getCalculations(): CalculatedBalancesArray {
    return this.calculations;
  }

  protected clearCalculations(): void {
    this.calculations = [];
    this.calculationsIns = [];
    this.calculationsOuts = [];
    this._balances.set(null);
    // #4602: ScenarioChartComponent re-renders off the `balances` signal (toObservable in
    // scenario-chart.component.ts) but guards on `balancePoints`. Leaving this one populated
    // meant a clear() fired the re-render while the guard still saw the purged store's data,
    // so the chart redrew from it instead of taking the null path. Matches the
    // calculation-past sibling, which nulls both.
    this._balancePoints.set(null);
    this._ins.set(null);
    this._outs.set(null);
  }

  triggerChanges(): void {}

  private setBalances(balancePointArray: BalancePoint[][]): void {
    this.calculations = [];
    this.calculationsIns = [];
    this.calculationsOuts = [];
    this.balancePointsArray = [];

    for (const balancePoints of balancePointArray) {
      const balancesPointMap: Map<string, BalancePoint> = new Map();
      const balanceMap = new Map();

      balancePoints.forEach((balancePoint) => {
        balancesPointMap.set(balancePoint.plotDate.toString(), balancePoint);
        balanceMap.set(balancePoint.balanceDate.toString(), balancePoint.balance);
      });

      this.balancePointsArray.push(balancesPointMap);
      this.calculations.push(balanceMap);
    }
    this._balances.set(this.calculations);
    this._balancePoints.set(this.balancePointsArray);
    this._ins.set(this.calculationsIns);
    this._outs.set(this.calculationsOuts);
  }

  clone(): CalculatedBalancesArray {
    return structuredClone(this.calculations);
  }
}
